import { getCountryOptionsWithAdjectives } from '@/utils/countriesUtility';

// ===== UTILITY FUNCTIONS =====

/**
 * Formats field labels to show required (*) or optional (Optional) status
 * @param {string} label - The base label text
 * @param {boolean} required - Whether the field is required
 * @returns {string} - Formatted label
 */
const formatFieldLabel = (label, required = true) => {
  // Remove existing asterisks to avoid duplication
  const cleanLabel = label.replace(/\s*\*\s*$/, '');

  if (required) {
    return `${cleanLabel} *`;
  } else {
    return `${cleanLabel} (Optional)`;
  }
};

/**
 * Basic input sanitization to prevent XSS attacks
 * @param {any} input - Input value to sanitize
 * @returns {any} - Sanitized input
 */
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return (
    input
      // Remove dangerous HTML tags and scripts
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
      .replace(/<link\b[^<]*(?:(?!<\/link>)<[^<]*)*<\/link>/gi, '')
      .replace(/<meta\b[^<]*(?:(?!<\/meta>)<[^<]*)*<\/meta>/gi, '')
      // Remove dangerous protocols
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')
      .replace(/data:image\/svg\+xml/gi, '') // Remove SVG data URLs but keep other data URLs
      // Remove dangerous event handlers
      .replace(/on\w+\s*=/gi, '')
      // Only remove script tags and keep everything else including spaces
      .replace(/<\/?script[^>]*>/gi, '')
      // Don't remove < > entirely, just escape them for safety
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  );
};

/**
 * Get field-specific validation rules based on field type and name
 * @param {Object} field - Field configuration object
 * @returns {Array} - Array of validation functions
 */
export const getFieldValidationRules = (field) => {
  const rules = [];

  // Add required validation if field is required
  if (field.required) {
    rules.push((value) => {
      if (!value || (typeof value === 'string' && !value.trim())) {
        return 'This field is required.';
      }
      return true;
    });
  }

  // Add field-type specific validations
  switch (field.type) {
    case 'text':
      rules.push(...getTextValidationRules(field));
      break;
    case 'textarea':
      rules.push(...getTextareaValidationRules(field));
      break;
    case 'select':
      rules.push(...getSelectValidationRules(field));
      break;
    case 'file':
      // File validation handled separately in components
      break;
    default:
      break;
  }

  return rules;
};

// ===== VALIDATION RULE HELPERS =====

/**
 * Text field validation rules
 */
const getTextValidationRules = (field) => {
  const rules = [];

  // Common text validation - sanitization check
  rules.push((value) => {
    if (!value) return true; // Skip if empty (required validation handles this)

    const sanitized = sanitizeInput(value);
    if (sanitized !== value) {
      return 'Invalid characters detected.';
    }

    return true;
  });

  // Field-specific text validations
  switch (field.name) {
    case 'role':
    case 'company_name':
    case 'currentCompany':
    case 'currentPosition':
      rules.push((value) => {
        if (!value) return true;

        // Allow letters, numbers, spaces, basic punctuation
        const regex = /^[a-zA-Z0-9\s.,'-]+$/;
        if (!regex.test(value)) {
          return 'Only letters, numbers, spaces, and basic punctuation allowed.';
        }

        if (value.length > 100) {
          return 'Maximum 100 characters allowed.';
        }

        return true;
      });
      break;

    case 'price':
    case 'pricePerYear':
    case 'kilometers':
    case 'kilometres':
      rules.push((value) => {
        if (!value) return true;

        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return 'Must be a valid number.';
        }

        if (numValue < 0) {
          return 'Value must be positive.';
        }

        if (numValue > 999999999) {
          return 'Value is too large.';
        }

        return true;
      });
      break;

    case 'phoneNumber':
    case 'phone':
      rules.push((value) => {
        if (!value) return true;

        // East African phone number validation
        const regex = /^\+(252|254|255|256|257|211|250|251)[0-9]{7,12}$/;
        if (!regex.test(value)) {
          return 'Enter a valid East African phone number with country code.';
        }

        return true;
      });
      break;

    case 'year':
      rules.push((value) => {
        if (!value) return true;

        const yearNum = parseInt(value);
        const currentYear = new Date().getFullYear();

        if (isNaN(yearNum)) {
          return 'Must be a valid year.';
        }

        if (yearNum < 1900 || yearNum > currentYear + 1) {
          return `Year must be between 1900 and ${currentYear + 1}.`;
        }

        return true;
      });
      break;

    case 'size':
      rules.push((value) => {
        if (!value) return true;

        // Allow numbers with common area units
        const regex =
          /^[0-9.,\s]+(sq\s?ft|sqft|sq\s?m|sqm|ft²|m²|square\s?feet|square\s?meters?)?$/i;
        if (!regex.test(value)) {
          return 'Enter a valid size (e.g.,  "100 sq m").';
        }

        return true;
      });
      break;

    case 'bedrooms':
    case 'bathrooms':
      rules.push((value) => {
        if (!value) return true;

        const numValue = parseInt(value);
        if (isNaN(numValue)) {
          return 'Must be a valid number.';
        }

        if (numValue < 0 || numValue > 50) {
          return 'Value must be between 0 and 50.';
        }

        return true;
      });
      break;

    case 'plateNumber':
      rules.push((value) => {
        if (!value) return true;

        // Allow alphanumeric characters, spaces, hyphens
        const regex = /^[A-Z0-9\s-]+$/i;
        if (!regex.test(value)) {
          return 'Only letters, numbers, spaces, and hyphens allowed.';
        }

        if (value.length > 20) {
          return 'Plate number too long.';
        }

        return true;
      });
      break;

    default:
      // Generic text validation for unknown fields
      rules.push((value) => {
        if (!value) return true;

        if (value.length > 255) {
          return 'Maximum 255 characters allowed.';
        }

        return true;
      });
      break;
  }

  return rules;
};

/**
 * Textarea field validation rules
 */
/**
 * Textarea field validation rules
 */
const getTextareaValidationRules = (field) => {
  const rules = [];

  // Don't apply sanitization check for textarea fields - let users type naturally
  // Only validate content structure and length

  // Field-specific textarea validations
  switch (field.name) {
    case 'cvSummary':
      rules.push((value) => {
        if (!value || !value.trim()) return true;

        const words = value
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0);

        if (words.length < 10) {
          return 'CV Summary should contain at least 10 words.';
        }

        if (words.length > 500) {
          return 'CV Summary cannot exceed 500 words.';
        }

        return true;
      });
      break;

    case 'job_description':
      rules.push((value) => {
        if (!value || !value.trim()) return true;

        const words = value
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0);

        if (words.length < 10) {
          return 'Job description should contain at least 10 words.';
        }

        if (words.length > 800) {
          return 'Job description cannot exceed 800 words.';
        }

        return true;
      });
      break;

    case 'skills':
      rules.push((value) => {
        if (!value || !value.trim()) return true;

        if (value.length > 1000) {
          return 'Skills section cannot exceed 1,000 characters.';
        }

        return true;
      });
      break;

    default:
      rules.push((value) => {
        if (!value || !value.trim()) return true;

        if (value.length > 5000) {
          return 'Maximum 5,000 characters allowed.';
        }

        return true;
      });
      break;
  }

  return rules;
};

/**
 * Select field validation rules
 */
const getSelectValidationRules = (field) => {
  const rules = [];

  // Validate against allowed options
  rules.push((value) => {
    if (!value) return true;

    if (field.options && Array.isArray(field.options)) {
      // Handle both string arrays and object arrays
      const allowedValues = field.options.map((option) =>
        typeof option === 'object' ? option.value : option
      );

      if (!allowedValues.includes(value)) {
        return 'Please select a valid option.';
      }
    }

    return true;
  });

  return rules;
};

// ===== FIELD DEFINITIONS =====

/**
 * Main function to get fields by category, subcategory, group, and section
 */
export const getFieldsByCategory = (
  categoryId,
  subcategoryId,
  groupId,
  sectionId,
  subcategorySlug
) => {
  console.log('Fetching fields for:', {
    categoryId,
    subcategoryId,
    groupId,
    sectionId,
    subcategorySlug,
  });

  // Validate categoryId
  if (!categoryId || isNaN(Number(categoryId))) {
    console.error('Invalid or missing categoryId:', categoryId);
    return [];
  }

  // Define category mapping using both numeric IDs and slugs
  const categoryMapping = {
    1: 'motors',
    2: 'jobs',
    3: 'property-for-sale',
    4: 'property-for-rent',
    5: 'community',
    6: 'classified',
    motors: 'motors',
    jobs: 'jobs',
    'property-for-sale': 'property-for-sale',
    'property-for-rent': 'property-for-rent',
    classified: 'classified',
    community: 'community',
  };

  // Map categoryId to categoryKey
  const categoryKey = categoryMapping[categoryId] || categoryMapping[String(categoryId)];
  if (!categoryKey) {
    console.error('Unknown categoryId:', categoryId);
    return [];
  }

  console.log('Mapped categoryKey:', categoryKey);

  // Define subcategory mapping for "Property for Sale"
  const propertyForSaleSubcategoryMapping = {
    7: 'residential', // Residential
    8: 'commercial', // Commercial
    9: 'land', // Land
    10: 'multiple-units', // Multiple Units
  };

  // Define fields for "Motors" with enhanced labels and validation
  const motorsFields = {
    2: [
      // Motorcycles
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 4,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 5,
        name: 'description',
        label: formatFieldLabel('Describe your Motorcycle', true),
        type: 'textarea',
        placeholder: 'Describe your motorcycle*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 6,
        name: 'usage',
        label: formatFieldLabel('Usage', true),
        type: 'select',
        options: [
          'Still with the dealer',
          'Only used once since it was purchased new',
          'Used very rarely since it was purchased',
          'Used once or twice a week since purchased',
          'Used as a primary mode of transportation',
        ],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'condition',
        label: formatFieldLabel('Condition', true),
        type: 'select',
        options: [
          'Perfect inside and out',
          'Almost no noticeable problems or flaws',
          'A bit of wear and tear, but in a good working condition',
          'Normal wear and tear for the age of the item, a few problems here and there',
          'Above average wear and tear. The item may need a bit of repair to work properly',
        ],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 8,
        name: 'kilometres',
        label: formatFieldLabel('Kilometres', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 9,
        name: 'year',
        label: formatFieldLabel('Year', true),
        type: 'select',
        options: Array.from({ length: 105 }, (_, i) => 2025 - i),
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 10,
        name: 'sellerType',
        label: formatFieldLabel('Seller Type', true),
        type: 'select',
        options: ['Owner', 'Dealer', 'Dealership / Certified Pre-owned'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 11,
        name: 'warranty',
        label: formatFieldLabel('Warranty', true),
        type: 'select',
        options: ['Yes', 'No', 'Does not apply'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 12,
        name: 'finalDriveSystem',
        label: formatFieldLabel('Final Drive System', false),
        type: 'select',
        options: ['Belt', 'Chain', 'Shaft', 'Does not apply'],
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 13,
        name: 'wheels',
        label: formatFieldLabel('Wheels', false),
        type: 'select',
        options: ['2 Wheels', '3 Wheels', '4 Wheels'],
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 14,
        name: 'manufacturer',
        label: formatFieldLabel('Manufacturer', true),
        type: 'select',
        options: ['Yamaha', 'Honda', 'Suzuki', 'Kawasaki'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 15,
        name: 'engineSize',
        label: formatFieldLabel('Engine Size', true),
        type: 'select',
        options: [
          'Less than 250cc',
          '250cc - 500cc',
          '500cc - 750cc',
          '750cc - 1000cc',
          '1000cc - 1500cc',
          '1500cc and above',
        ],
        required: true,
        validation: getFieldValidationRules,
      },
    ],
    3: [
      // Auto Accessories & Parts
      {
        id: 16,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 17,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 19,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 20,
        name: 'description',
        label: formatFieldLabel('Describe your Auto Accessory', true),
        type: 'textarea',
        placeholder: 'Describe your auto accessory*',
        required: true,
        validation: getFieldValidationRules,
      },
    ],
    4: [
      // Heavy Vehicles
      {
        id: 23,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 24,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 26,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 27,
        name: 'description',
        label: formatFieldLabel('Describe your Heavy Vehicle', true),
        type: 'textarea',
        placeholder: 'Describe your heavy vehicle*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 28,
        name: 'fuelType',
        label: formatFieldLabel('Fuel Type', true),
        type: 'select',
        options: ['Petrol', 'Diesel', 'Hybrid', 'Electric'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 29,
        name: 'kilometres',
        label: formatFieldLabel('Kilometres', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 30,
        name: 'year',
        label: formatFieldLabel('Year', true),
        type: 'select',
        options: Array.from({ length: 105 }, (_, i) => 2025 - i),
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 31,
        name: 'sellerType',
        label: formatFieldLabel('Seller Type', true),
        type: 'select',
        options: ['Owner', 'Dealer', 'Dealership / Certified Pre-owned'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 32,
        name: 'warranty',
        label: formatFieldLabel('Warranty', true),
        type: 'select',
        options: ['Yes', 'No', 'Does not apply'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 33,
        name: 'numberOfCylinders',
        label: formatFieldLabel('No. Of Cylinders', false),
        type: 'select',
        options: ['3', '4', '5', '6', '8', '10', '12', 'Unknown'],
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 37,
        name: 'make',
        label: formatFieldLabel('Make', true),
        type: 'select',
        options: [],
        required: true,
        dynamic: 'heavyVehicleMake',
        validation: getFieldValidationRules,
      },
      {
        id: 34,
        name: 'model',
        label: formatFieldLabel('Model', true),
        type: 'select',
        options: [],
        required: true,
        dynamic: 'heavyVehicleModel',
        validation: getFieldValidationRules,
      },
      {
        id: 35,
        name: 'condition',
        label: formatFieldLabel('Body Condition', true),
        type: 'select',
        options: [
          'Perfect inside and out',
          'No accidents, very few faults',
          'A bit of wear and tear, all repaired',
          'Normal wear & tear , all repaired ',
          'Lots of wear & tear to the body',
        ],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 36,
        name: 'mechanicalCondition',
        label: formatFieldLabel('Mechanical Condition', false),
        type: 'select',
        options: ['Belt', 'Chain', 'Shaft', 'Does not apply'],
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 38,
        name: 'capacity',
        label: formatFieldLabel('Capacity/Weight', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 39,
        name: 'horsepower',
        label: formatFieldLabel('Horsepower', false),
        type: 'select',
        options: [
          'Less than 150 HP',
          '150 - 199 HP',
          '200 - 299 HP',
          '300 - 399 HP',
          '400+ HP',
          'Unknown',
        ],
        required: false,
        validation: getFieldValidationRules,
      },
    ],
    5: [
      // Boats
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Enter the title',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 4,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        placeholder: 'Enter the price',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 5,
        name: 'description',
        label: formatFieldLabel('Describe your Boat', true),
        type: 'textarea',
        placeholder: 'Describe your boat',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 6,
        name: 'age',
        label: formatFieldLabel('Age', true),
        type: 'select',
        options: [
          'Less than 1 month',
          '1-3 months',
          '3-6 months',
          '6-12 months',
          '1-2 years',
          '2-5 years',
          '5-10 years',
          '10+ years',
        ],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'usage',
        label: formatFieldLabel('Usage', true),
        type: 'select',
        options: ['Still with the dealer', 'Rarely used', 'Used frequently'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 8,
        name: 'condition',
        label: formatFieldLabel('Condition', true),
        type: 'select',
        options: ['Perfect inside and out', 'Minor wear and tear', 'Needs some repairs'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 9,
        name: 'sellerType',
        label: formatFieldLabel('Seller Type', true),
        type: 'select',
        options: ['Owner', 'Dealer'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 10,
        name: 'warranty',
        label: formatFieldLabel('Warranty', true),
        type: 'select',
        options: ['Yes', 'No'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 11,
        name: 'length',
        label: formatFieldLabel('Length', true),
        type: 'select',
        options: ['Less than 10 ft', '10-20 ft', '20-30 ft', '30-40 ft', '40+ ft'],
        required: true,
        validation: getFieldValidationRules,
      },
    ],
    6: [
      // Number Plates
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Enter the title',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'plateCode',
        label: formatFieldLabel('Plate Code', true),
        type: 'custom',
        component: 'PlateCodeDropdown',
        required: true,
      },
      {
        id: 3,
        name: 'plateNumber',
        label: formatFieldLabel('Plate Number', true),
        type: 'text',
        placeholder: 'Plate number',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 4,
        name: 'designType',
        label: formatFieldLabel('Design Type', true),
        type: 'custom',
        component: 'DesignToggle',
        required: true,
      },
      {
        id: 5,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 6,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        placeholder: 'Enter the price',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'description',
        label: formatFieldLabel('Describe your item', true),
        type: 'textarea',
        placeholder: 'Describe your number plate',
        required: true,
        validation: getFieldValidationRules,
      },
    ],
  };

  // Define fields for "Property for Sale" with enhanced labels and validation
  const propertyForSaleFields = {
    residential: [
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 4,
        name: 'youtubeUrl',
        label: formatFieldLabel('YouTube URL', false),
        type: 'text',
        placeholder: 'Optional',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 6,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'description',
        label: formatFieldLabel('Describe your Property', true),
        type: 'textarea',
        placeholder: 'Describe your property*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 8,
        name: 'size',
        label: formatFieldLabel('Size in sq m', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 9,
        name: 'bedrooms',
        label: formatFieldLabel('Bedrooms', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 10,
        name: 'bathrooms',
        label: formatFieldLabel('Bathrooms', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 11,
        name: 'developer',
        label: formatFieldLabel('Developer', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 12,
        name: 'readyBy',
        label: formatFieldLabel('Ready By', false),
        type: 'date',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 13,
        name: 'isFurnished',
        label: formatFieldLabel('Is it Furnished?', true),
        type: 'select',
        options: ['Yes', 'No'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 14,
        name: 'occupancyStatus',
        label: formatFieldLabel('Occupancy Status', true),
        type: 'select',
        options: ['Vacant', 'Occupied'],
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 15,
        name: 'totalClosingFee',
        label: formatFieldLabel('Total Closing Fee In USD', false),
        type: 'text',
        placeholder: 'Enter the total closing fee',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 16,
        name: 'annualCommunityFee',
        label: formatFieldLabel('Annual Community Fee In USD', false),
        type: 'text',
        placeholder: 'Enter the annual community fee',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 17,
        name: 'propertyReferenceId',
        label: formatFieldLabel('Property Reference ID', false),
        type: 'text',
        placeholder: 'Enter the property reference ID',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 18,
        name: 'sellerTransferFee',
        label: formatFieldLabel('Seller Transfer Fee In USD', false),
        type: 'text',
        placeholder: 'Enter the seller transfer fee',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 19,
        name: 'maintenanceFee',
        label: formatFieldLabel('Maintenance Fee In USD ', false),
        type: 'text',
        placeholder: 'Enter the maintenance fee',
        required: false,
        validation: getFieldValidationRules,
      },
    ],
    commercial: [], // Will be set to residential fields below
    land: [
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 4,
        name: 'youtubeUrl',
        label: formatFieldLabel('YouTube URL', false),
        type: 'text',
        placeholder: 'Optional',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 6,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'description',
        label: formatFieldLabel('Describe your Property', true),
        type: 'textarea',
        placeholder: 'Describe your property*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 8,
        name: 'size',
        label: formatFieldLabel('Size in sq m', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 9,
        name: 'closingFee',
        label: formatFieldLabel('Total Closing Fee in USD', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 10,
        name: 'propertyReferenceId',
        label: formatFieldLabel('Property Reference ID#', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 11,
        name: 'buyerTransferFee',
        label: formatFieldLabel('Buyer Transfer Fee In USD', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 12,
        name: 'sellerTransferFee',
        label: formatFieldLabel('Seller Transfer Fee In USD', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 13,
        name: 'zonedFor',
        label: formatFieldLabel('Zoned For', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 14,
        name: 'approvedBuildUpArea',
        label: formatFieldLabel('Approved Build Up Area Size', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
    ],
    'multiple-units': [
      {
        id: 1,
        name: 'title',
        label: formatFieldLabel('Title', true),
        type: 'text',
        placeholder: 'Title*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 2,
        name: 'images',
        label: formatFieldLabel('Add Pictures', true),
        type: 'file',
        required: true,
      },
      {
        id: 4,
        name: 'youtubeUrl',
        label: formatFieldLabel('YouTube URL', false),
        type: 'text',
        placeholder: 'Optional',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 6,
        name: 'price',
        label: formatFieldLabel('Price', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 7,
        name: 'description',
        label: formatFieldLabel('Describe your Property', true),
        type: 'textarea',
        placeholder: 'Describe your property*',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 8,
        name: 'size',
        label: formatFieldLabel('Size in sq m', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 9,
        name: 'closingFee',
        label: formatFieldLabel('Total Closing Fee In USD', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 10,
        name: 'propertyReferenceId',
        label: formatFieldLabel('Property Reference ID#', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 11,
        name: 'buyerTransferFee',
        label: formatFieldLabel('Buyer Transfer Fee In USD', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
      {
        id: 12,
        name: 'sellerTransferFee',
        label: formatFieldLabel('Seller Transfer Fee In USD', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 13,
        name: 'zonedFor',
        label: formatFieldLabel('Zoned For', true),
        type: 'text',
        required: true,
        validation: getFieldValidationRules,
      },
      {
        id: 14,
        name: 'approvedBuildUpArea',
        label: formatFieldLabel('Approved Build Up Area Size', false),
        type: 'text',
        required: false,
        validation: getFieldValidationRules,
      },
    ],
  };

  // Define fields for "Property for Rent" with enhanced labels and validation
  const propertyForRentFields = [
    {
      id: 1,
      name: 'title',
      label: formatFieldLabel('Title', true),
      type: 'text',
      placeholder: 'Title*',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 2,
      name: 'images',
      label: formatFieldLabel('Add Pictures', true),
      type: 'file',
      required: true,
    },
    {
      id: 4,
      name: 'pricePerYear',
      label: formatFieldLabel('Rent', true),
      type: 'text',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 5,
      name: 'description',
      label: formatFieldLabel('Describe your Property', true),
      type: 'textarea',
      placeholder: 'Describe your property*',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 6,
      name: 'size',
      label: formatFieldLabel('Size in sq m', true),
      type: 'text',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 7,
      name: 'bedrooms',
      label: formatFieldLabel('Bedrooms', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 8,
      name: 'bathrooms',
      label: formatFieldLabel('Bathrooms', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 9,
      name: 'isFurnished',
      label: formatFieldLabel('Is it Furnished?', true),
      type: 'select',
      options: ['Yes', 'No'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 10,
      name: 'rentPaid',
      label: formatFieldLabel('Rent is Paid', false),
      type: 'select',
      options: ['Yearly', 'Bi-Yearly', 'Quarterly', 'Monthly'],
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 11,
      name: 'propertyReferenceId',
      label: formatFieldLabel('Property Reference ID', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 12,
      name: 'minimumContractPeriod',
      label: formatFieldLabel('Minimum Contract Period In Months', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 13,
      name: 'noticePeriod',
      label: formatFieldLabel('Notice Period In Months', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 14,
      name: 'maintenanceFee',
      label: formatFieldLabel('Maintenance Fee In USD', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 15,
      name: 'occupancyStatus',
      label: formatFieldLabel('Occupancy Status', false),
      type: 'select',
      options: ['Vacant', 'Occupied'],
      required: false,
      validation: getFieldValidationRules,
    },
    // {
    //   id: 16,
    //   name: 'propertyType',
    //   label: formatFieldLabel('Property Type', false),
    //   type: 'text',
    //   required: false,
    //   validation: getFieldValidationRules,
    // },
    // { id: 18, name: "city_id", label: formatFieldLabel("City", true), type: "text", required: true, validation: getFieldValidationRules },
    // { id: 19, name: "rera_broker_id", label: formatFieldLabel("RERA Broker ID", false), type: "text", required: false, validation: getFieldValidationRules },
    {
      id: 20,
      name: 'company_license_number',
      label: formatFieldLabel('Company License Number', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 21,
      name: 'company_telephone_number',
      label: formatFieldLabel('Company Telephone Number', false),
      type: 'text',
      required: false,
      validation: getFieldValidationRules,
    },
  ];

  // Define fields for Jobs with enhanced labels and validation
  const jobsFields = [
    {
      id: 1,
      name: 'title',
      label: formatFieldLabel('Job Title', true),
      type: 'text',
      placeholder: 'Enter the job title',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 3,
      name: 'cvSummary',
      label: formatFieldLabel('CV Summary', true),
      type: 'textarea',
      placeholder: 'Write a summary of your CV',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 4,
      name: 'gender',
      label: formatFieldLabel('Gender', true),
      type: 'select',
      options: ['Male', 'Female', 'Other'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 5,
      name: 'nationality',
      label: formatFieldLabel('Nationality', true),
      type: 'select',
      options: getCountryOptionsWithAdjectives(),
      placeholder: 'Select your nationality',
      searchable: true,
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 6,
      name: 'cv',
      label: formatFieldLabel('Upload Your CV', true),
      type: 'file',
      subtitle: 'Drag and Drop or Click to Select',
      placeholder: 'File should be .docx, .doc, .pdf, or image files below 5MB',
      style: {
        border: '2px dotted blue',
        padding: '16px',
        textAlign: 'center',
      },
      accept: '.pdf,image/*',
      required: true,
    },
    {
      id: 8,
      name: 'currentCompany',
      label: formatFieldLabel('Current Company', false),
      type: 'text',
      placeholder: 'Enter your current company',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 9,
      name: 'currentPosition',
      label: formatFieldLabel('Current Position', true),
      type: 'text',
      placeholder: 'Enter your current position',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 10,
      name: 'noticePeriod',
      label: formatFieldLabel('Notice Period In Months', true),
      type: 'select',
      options: ['Available Immediately', 'Less than 2 weeks', '1 Month', 'More Than 1 Month'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 11,
      name: 'visaStatus',
      label: formatFieldLabel('Visa Status', true),
      type: 'select',
      options: [
        'Not Applicable',
        'Business Visa',
        'Employment Visa',
        'Resident Visa',
        'Spouse Visa',
        'Work Visa',
        'Student Visa',
        'Tourist Visa',
        'Visit',
      ],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 12,
      name: 'careerLevel',
      label: formatFieldLabel('Career Level', true),
      type: 'text',
      placeholder: 'Enter your career level (e.g., Entry, Mid, Senior)',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 13,
      name: 'salaryExpectations',
      label: formatFieldLabel('Expected Salary', true),
      type: 'select',
      options: [
        'Negotiable',
        'Less than 2,000',
        '2,000 - 3,999',
        '4,000 - 5,999',
        '6,000 - 7,999',
        '8,000 - 11,999',
        '12,000 - 19,999',
        '20,000+',
      ],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 14,
      name: 'workExperience',
      label: formatFieldLabel('Work Experience', true),
      type: 'select',
      options: ['0-1 Years', '1-2 Years', '2-5 Years', '5-10 Years', '10-15 Years', '15+ Years'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 15,
      name: 'educationLevel',
      label: formatFieldLabel('Education Level', true),
      type: 'select',
      options: ['N/A', 'High School', 'Diploma', "Bachelor's", "Master's", 'PhD'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 16,
      name: 'commitment',
      label: formatFieldLabel('Commitment', true),
      type: 'select',
      options: ['Full Time', 'Part Time', 'Contract', 'Temporary', 'Other', 'Freelance'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 17,
      name: 'desiredLocation',
      label: formatFieldLabel('Preferred Work Location', false),
      type: 'text',
      placeholder: 'Enter your preferred work location (e.g., Mogadishu, Remote)',
      required: false,
      validation: getFieldValidationRules,
    },
  ];

  // Define fields for Community with enhanced labels and validation
  const communityFields = [
    {
      id: 1,
      name: 'title',
      label: formatFieldLabel('Title', true),
      type: 'text',
      placeholder: 'Enter the title',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 2,
      name: 'images',
      label: formatFieldLabel('Add Pictures', true),
      type: 'file',
      required: true,
    },
    {
      id: 3,
      name: 'description',
      label: formatFieldLabel('Describe your item', true),
      type: 'textarea',
      placeholder: 'Describe your item',
      required: true,
      validation: getFieldValidationRules,
    },

    {
      id: 4,
      name: 'price',
      label: formatFieldLabel('Price', true),
      type: 'text',
      required: true,
      validation: getFieldValidationRules,
    },
  ];

  // Define fields for Classified with enhanced labels and validation
  const classifiedFields = [
    {
      id: 1,
      name: 'title',
      label: formatFieldLabel('Title', true),
      type: 'text',
      placeholder: 'Enter the title',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 2,
      name: 'images',
      label: formatFieldLabel('Add Pictures', true),
      type: 'file',
      required: true,
    },
    {
      id: 3,
      name: 'phoneNumber',
      label: formatFieldLabel('Contact number', true),
      type: 'text',
      placeholder: 'Contact number',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      id: 4,
      name: 'price',
      label: formatFieldLabel('Price', true),
      type: 'text',
      placeholder: 'Enter the price',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 5,
      name: 'description',
      label: formatFieldLabel('Describe your item', true),
      type: 'textarea',
      placeholder: 'Describe your item',
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 6,
      name: 'age',
      label: formatFieldLabel('Age', true),
      type: 'select',
      options: [
        'Brand New',
        '0-1 month',
        '1-6 months',
        '6-12 months',
        '1-2 years',
        '2-5 years',
        '5-10 years',
        '10+ years',
      ],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 7,
      name: 'usage',
      label: formatFieldLabel('Usage', true),
      type: 'select',
      options: ['Never used', 'Used once', 'Light Usage', 'Heavy Usage', 'Other'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 8,
      name: 'condition',
      label: formatFieldLabel('Condition', true),
      type: 'select',
      options: ['Flawless', 'Excellent', 'Good', 'Average', 'Poor'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 9,
      name: 'warranty',
      label: formatFieldLabel('Warranty', true),
      type: 'select',
      options: ['Yes', 'No', 'Does not apply'],
      required: true,
      validation: getFieldValidationRules,
    },
    {
      id: 9,
      name: 'brand',
      label: formatFieldLabel('Brand', false),
      type: 'select',
      options: [
        'AMD',
        'ASUS',
        'ATI',
        'Acer',
        'Adobe',
        'Alienware',
        'Apple/Mac',
        'BENQ',
        'Belkin',
        'Brother',
        'Canon',
        'Compaq',
        'D-Link',
        'Dell',
        'Fujistu',
        'Gateway',
        'HP',
        'IBM/Lenovo',
        'Intel',
        'Lexmark',
        'Linksys',
        'Logitech',
        'Macromedia',
        'Microsoft',
        'NEC',
        'OKI',
        'Packard Bell',
        'Panasonic',
        'Samsung',
        'Sony',
        'Toshiba',
        'Western Digital',
        'Xerox',
        'eMachines',
        'Öther',
      ],
      required: false,
      validation: getFieldValidationRules,
    },
    // {
    //   id: 10,
    //   name: 'modelName',
    //   label: formatFieldLabel('Model name*', true),
    //   type: 'text',
    //   placeholder: 'Enter the title',
    //   required: true,
    //   validation: getFieldValidationRules,
    // },
    // {
    //   id: 11,
    //   name: 'processor',
    //   label: formatFieldLabel('Processor Type*', true),
    //   type: 'select',
    //   options: [
    //     'AMD Athlon',
    //     'AMD FX',
    //     'AMD Ryzen 3',
    //     'AMD Ryzen 5',
    //     'AMD Ryzen 7',
    //     'AMD Ryzen 9',
    //     'AMD Threadripper',
    //     'ARM Cortex',
    //     'Apple M1',
    //     'Apple M1 Max',
    //     'Apple M1 Pro',
    //     'Apple M3',
    //     'Apple M3 Max',
    //     'Apple M3 Pro',
    //     'Apple M4',
    //     'Apple M4 Max',
    //     'Apple M4 Pro',
    //     'Intel Celeron',
    //     'Intel Core i3',
    //     'Intel Core i5',
    //     'Intel Core i7',
    //     'Intel Core i9',
    //     'Intel Pentium',
    //     'Intel Xeon',
    //     'Qualcomm Snapdragon',
    //     'Other',
    //   ],
    //   required: true,
    //   validation: getFieldValidationRules,
    // },
    // {
    //   id: 12,
    //   name: 'displayResolution',
    //   label: formatFieldLabel('Display Resolution*', true),
    //   type: 'select',
    //   options: [
    //     '1280 x 720 pixel',
    //     '1280 x 800 pixel',
    //     '1366 x 768 pixel',
    //     '1366 x 912 pixel',
    //     '1440 x 900 pixel',
    //     '1536 x 1024 pixel',
    //     '1600 x 900 pixel',
    //     '1900 x 1200 pixel',
    //     '1920 x 1080 pixel',
    //     '1920 x 1200 pixel',
    //     '2256 x 1504 pixel',
    //     '2400 x 1600 pixel',
    //     '2496 x 1664 pixel',
    //     '2560 x 1080 pixel',
    //     '2560 x 1440',
    //     '2560 x 1600 pixel',
    //     '2560 x 1700 pixel',
    //     '2736 x 1824 pixel',
    //     '2880 x 1800 pixel',
    //     '2880 x 1920 pixel',
    //     '3000 x 2000 pixel',
    //     '3072 x 1920 pixel',
    //     '3456 x 2160 pixel',
    //     '3840 x 2160 pixel',
    //     '3840 x 2400 pixel',
    //   ],
    //   required: true,
    //   validation: getFieldValidationRules,
    // },
    // {
    //   id: 13,
    //   name: 'displayResolutionType',
    //   label: formatFieldLabel('Display Resolution Type*', true),
    //   type: 'select',
    //   options: [
    //     '4.5K',
    //     '4K',
    //     '5K',
    //     '5K Retina',
    //     'DFHD',
    //     'DQHD',
    //     'Full HD',
    //     'HD',
    //     'HD+',
    //     'QHD',
    //     'Quad HD',
    //     'UHD',
    //     'UWQHD',
    //     'Ultra HD',
    //     'VGA',
    //     'WQHD',
    //     'WSXGA',
    //     'WSXGA',
    //     'WUHD',
    //     'WVGA',
    //     'WXGA',
    //     'Others',
    //   ],
    //   required: true,
    //   validation: getFieldValidationRules,
    // },

    // {
    //   id: 14,
    //   name: 'memory',
    //   label: formatFieldLabel('Memory (RAM)', findLastKey),
    //   type: 'select',
    //   options: [
    //     '512 MB',
    //     '1 GB',
    //     '2 GB',
    //     '4 GB',
    //     '8 GB',
    //     '16 GB',
    //     '32 GB',
    //     '64 GB',
    //     '128 GB',
    //     '256 GB',
    //     '512 GB',
    //     '1 TB',
    //     '2 TB',
    //     '3 TB',
    //     '4 TB',
    //     '6 TB',
    //     '8 TB',
    //     '12 TB',
    //     'Other amount',
    //   ],
    //   required: false,
    //   validation: getFieldValidationRules,
    // },

    // {
    //   id: 15,
    //   name: 'processorSpeed',
    //   label: formatFieldLabel('Processor Speed(Per Core)', false),
    //   type: 'select',
    //   options: ['Less tha 500 MHz', '500 MHz or more', '1 GHz or more', '2 GHz or more'],
    //   required: true,
    //   validation: getFieldValidationRules,
    // },
    // {
    //   id: 16,
    //   name: 'hardDrive',
    //   label: formatFieldLabel('Hard Drive', false),
    //   type: 'select',
    //   options: [
    //     '0 - 99 GB',
    //     '100 - 249 GB',
    //     '250 - 499 GB',
    //     '500 - 749 GB',
    //     '750 - 999 GB',
    //     '1 - 1.49 TB',
    //     '1.5 - 1.9 TB',
    //     '2+ TB',
    //   ],
    //   required: true,
    //   validation: getFieldValidationRules,
    // },
  ];

  // Electronics specific fields (subcategory 30)
  const classifiedElectronicsFields = [
    ...classifiedFields,
    // Electronics specific optional fields - TVs, Monitors, Cameras, Audio Equipment, etc.
    {
      name: 'resolution',
      label: formatFieldLabel('Resolution', false),
      type: 'text',
      placeholder: 'e.g., 1920x1080, 4K',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'model_name',
      label: formatFieldLabel('Model Name', false),
      type: 'text',
      placeholder: 'e.g., Samsung UN55TU8000',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'model_type',
      label: formatFieldLabel('Model Type', false),
      type: 'text',
      placeholder: 'e.g., Smart TV, Soundbar',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'screen_size',
      label: formatFieldLabel('Screen Size', false),
      type: 'text',
      placeholder: 'e.g., 55 inches, 27 inches',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'mount_size',
      label: formatFieldLabel('Mount Size/VESA', false),
      type: 'text',
      placeholder: 'e.g., 400x400mm',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'display_resolution',
      label: formatFieldLabel('Display Resolution', false),
      type: 'text',
      placeholder: 'e.g., Full HD, 4K UHD',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'display_resolution_type',
      label: formatFieldLabel('Display Technology', false),
      type: 'text',
      placeholder: 'e.g., LED, OLED, QLED',
      required: false,
      validation: getFieldValidationRules,
    },
  ];

  // Computers & Networking specific fields (subcategory 31)
  const classifiedComputersFields = [
    ...classifiedFields,
    // Computer/Networking specific optional fields - Laptops, Desktops, Servers, Routers, etc.
    {
      name: 'model_name',
      label: formatFieldLabel('Model Name', false),
      type: 'text',
      placeholder: 'e.g., MacBook Pro M2, ThinkPad X1',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'model_type',
      label: formatFieldLabel('Model Type', false),
      type: 'text',
      placeholder: 'e.g., Laptop, Desktop, Router',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'processor',
      label: formatFieldLabel('Processor/CPU', false),
      type: 'text',
      placeholder: 'e.g., Intel i7-12700K, AMD Ryzen 5',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'memory',
      label: formatFieldLabel('RAM Memory', false),
      type: 'text',
      placeholder: 'e.g., 16GB DDR4, 32GB',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'processor_speed',
      label: formatFieldLabel('Processor Speed', false),
      type: 'text',
      placeholder: 'e.g., 3.6 GHz, 2.8 GHz base',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'hard_drive',
      label: formatFieldLabel('Storage', false),
      type: 'text',
      placeholder: 'e.g., 512GB SSD, 1TB HDD',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'graphics_card',
      label: formatFieldLabel('Graphics Card', false),
      type: 'text',
      placeholder: 'e.g., NVIDIA RTX 4070, AMD Radeon',
      required: false,
      validation: getFieldValidationRules,
    },
    {
      name: 'operating_system',
      label: formatFieldLabel('Operating System', false),
      type: 'text',
      placeholder: 'e.g., Windows 11, macOS Ventura',
      required: false,
      validation: getFieldValidationRules,
    },
  ];
  // Define common fields for all categories
  const commonFields = [
    {
      id: 1001,
      name: 'paymentNumber',
      label: 'Please Pay a service fee of 50$ and Enter the Phone number Used for Payment',
      type: 'text',
      placeholder: 'Enter phone number',
      required: true,
      validation: getFieldValidationRules,
    },
  ];

  // Reuse residential fields for commercial
  propertyForSaleFields.commercial = propertyForSaleFields.residential;

  // Determine fields based on category and subcategory
  let fields = [];
  if (categoryKey === 'motors' && motorsFields[subcategoryId]) {
    fields = motorsFields[subcategoryId];
  } else if (categoryKey === 'property-for-sale') {
    const subcategoryKey = propertyForSaleSubcategoryMapping[subcategoryId];

    if (!subcategoryKey) {
      console.error('Unknown subcategoryId for Property for Sale:', subcategoryId);
      return [];
    }
    console.log('Mapped subcategoryKey for Property for Sale:', subcategoryKey);
    fields = propertyForSaleFields[subcategoryKey] || [];
  } else if (categoryKey === 'property-for-rent') {
    // All subcategories for property-for-rent use the same fields
    fields = propertyForRentFields;
  } else if (categoryKey === 'jobs') {
    if (subcategorySlug === 'hiring') {
      fields = [
        {
          name: 'title',
          label: formatFieldLabel('Job title', true),
          type: 'text',
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'role',
          label: formatFieldLabel('Job role', true),
          type: 'text',
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'industry_id',
          label: formatFieldLabel('Industry', true),
          type: 'selectIndustry',
          options: [],
          required: true,
          dynamic: 'industry',
          validation: getFieldValidationRules,
        },
        {
          name: 'job_description',
          label: formatFieldLabel('Job Description', true),
          type: 'textarea',
          required: true,
        },
        {
          name: 'gender',
          label: formatFieldLabel('Gender', true),
          type: 'select',
          options: ['Male', 'Female', 'Any'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'commitment',
          label: formatFieldLabel('Employment Type', true),
          type: 'select',
          options: ['Full Time', 'Part Time', 'Contract', 'Temporary'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'remote',
          label: formatFieldLabel('Remote Job', true),
          type: 'select',
          options: ['Yes', 'No'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'min_work_experience',
          label: formatFieldLabel('Minimum Work Experience', true),
          type: 'select',
          options: [
            '0-1 Years',
            '1-2 Years',
            '2-5 Years',
            '5-10 Years',
            '10-15 Years',
            '15+ Years',
          ],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'educationLevel',
          label: formatFieldLabel('Minimum Education Level', true),
          type: 'select',
          options: ['N/A', 'High-School / Secondary', 'Bachelors Degree', 'Masters Degree', 'PhD'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'salaryExpectations',
          label: formatFieldLabel('Monthly Salary', true),
          type: 'select',
          options: [
            'Negotiable',
            'Less than 2,000',
            '2,000 - 3,999',
            '4,000 - 5,999',
            '6,000 - 7,999',
            '8,000 - 11,999',
            '12,000 - 19,999',
            '20,000+',
          ],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'company_name',
          label: formatFieldLabel('Company Name', true),
          type: 'text',
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'isHideCompany',
          label: formatFieldLabel('Hide Company Name', true),
          type: 'select',
          options: ['Yes', 'No'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'company_size_id',
          label: formatFieldLabel('Company Size', true),
          type: 'selectCompanySize',
          options: [],
          required: true,
          dynamic: 'companySize',
          validation: getFieldValidationRules,
        },
        {
          name: 'nationality',
          label: formatFieldLabel('Preferred Nationality', false),
          type: 'select',
          options: getCountryOptionsWithAdjectives(),
          placeholder: 'Select preferred nationality',
          searchable: true,
          required: false,
          validation: getFieldValidationRules,
        },
        {
          name: 'languages',
          label: formatFieldLabel('Languages', false),
          type: 'custom',
          component: 'LanguageCheckboxes',
          required: false,
        },
        {
          name: 'benefits',
          label: formatFieldLabel('Benefits', false),
          type: 'custom',
          component: 'BenefitsCheckboxes',
          required: false,
        },
        {
          name: 'isCvRequired',
          label: formatFieldLabel('CV Required', true),
          type: 'select',
          options: ['Yes', 'No'],
          required: true,
          validation: getFieldValidationRules,
        },
        {
          name: 'skills',
          label: formatFieldLabel('Skills', false),
          type: 'textarea',
          placeholder: 'Vue,React,Node, etc. separated by commas',
          required: false,
          validation: getFieldValidationRules,
        },
      ];
    } else {
      fields = jobsFields;
    }
  } else if (categoryKey === 'community') {
    fields = communityFields;
  } else if (categoryKey === 'classified') {
    // Use subcategoryKey for matching with specific fields for each subcategory
    const subcategoryKey = `classified-${subcategoryId}`;

    if (subcategoryKey === 'classified-30') {
      // Electronics (subcategory 30) - TVs, Audio, Cameras, etc.
      fields = classifiedElectronicsFields;
    } else if (subcategoryKey === 'classified-31') {
      // Computers & Networking (subcategory 31) - Laptops, Desktops, Servers, etc.
      fields = classifiedComputersFields;
    } else if (subcategoryKey === 'classified-51') {
      // ✅ NEW: Mobile Phones & Tablets (subcategory 51) - Use electronics fields
      fields = classifiedElectronicsFields;
    } else if (subcategoryKey === 'classified-52') {
      // ✅ NEW: Furniture (subcategory 52) - Use generic classified fields
      fields = classifiedFields;
    } else {
      // All other classified subcategories use generic fields
      fields = classifiedFields;
    }
  } else {
    console.error('Unknown subcategoryId for category:', {
      categoryKey,
      subcategoryId,
    });
    return [];
  }
  // Append common fields to the category-specific fields
  return [...fields, ...commonFields];
};

// Export sanitization function for use in components
export { sanitizeInput };
